import { CloseCode } from "odyssey-shared";
import { say } from "../words.ts";
import { closeNotice, joinTitle, signInNotice, type CloseNotice, type CloseRow, type SignInEnd } from "./disconnect.ts";
import type { JoinAwait } from "./join.ts";
import type { LoadingFailure } from "./loading-state.ts";

/**
 * What the loading shell says when the page cannot reach the world (P1.4-T5; the brief's loading shell:
 * "on failure the category (sign-in, world, server) and reason"), and, since P1.9-T15, why the page left a
 * world it showed. Each failure is a notice title and one player sentence, with the specifics on the detail
 * line beneath: the close code and the server's own reason (PROTOCOL.md section 8), or the page's message for
 * the file that failed. Before the world is revealed, a refused token (4001) is the sign-in's failure, any
 * other close code the server sends is the server refusing, titled with its code (the author's ruling of
 * 2026-09-27, so a report of the title alone narrows the cause), a socket that never reached the server or lost
 * it without a close frame is the server being unreachable, as is a server that sends no welcome, or no
 * snapshot after it, within the join's deadline, and a file the world needs that fails to arrive or to read
 * is the world's failure; each offers Retry. A close keeps these titles and takes its sentence and detail
 * line from the disconnect table (`disconnect.ts`), so a code reads the same before the reveal and after it.
 * After the reveal, a close is the table's notice whole, with its action (`disconnectFailure`), and so is a
 * sign-in that ended while the page played (`signInFailure`).
 *
 * The words are drafts for the author (ADR-061, amendment "the export is a visual reference"): sentence
 * case, a full stop after a sentence and none after a title, and "server" the only word for the machines
 * behind the game (ADR-060). The unreachable sentence for a silent server is the page's "the server did not
 * answer" in sentence case.
 */

/**
 * The title of a close code the server sent before the reveal, other than a refused token: the refused title
 * with the code in parentheses.
 *
 * @param code The close code.
 * @returns The title.
 * @example
 * refusedTitle(4009);
 * // "Server refused (4009)"
 */
export function refusedTitle(code: number): string {
  return say("loading.failure.refusedCode", { title: { k: "loading.failure.refused" }, code: String(code) });
}

/**
 * Close codes the browser reports when no close frame came from the server: none was given (1005), the
 * connection was lost or never made (1006), or its TLS handshake failed (1015).
 */
const WITHOUT_SERVER: ReadonlySet<number> = new Set([1005, 1006, 1015]);

/**
 * The failure for a socket that closed before the world was revealed: the category's title over the
 * disconnect table's sentence and detail line for the code, a code the server sent titled with it, with
 * Retry; or with Reload where the table's notice reloads the page (a stale page refused at `hello`, a server frame it cannot read, a newer world
 * version, a code it does not know), since connecting again from a page out of step with the server fails
 * the same way; a full server and a full queue, which are no fault, under their own titles in place of the code's
 * (`joinTitle`), the code staying on the detail line for support; or with Log in for an account's refused sign-in, whose sentence asks the player to log in
 * again, as the same close does after the reveal; or with View status for maintenance (4011), which forgets the
 * tab's sign-in and reloads onto the maintenance page.
 *
 * @param code The close code, from the server's `error` message or the socket's close.
 * @param reason The server's reason, which may be empty.
 * @param account Whether the page played an account session, whose refused token means the player must log
 *   in again; a `?token=` page cannot.
 * @param told What the gateway told the page just before a close by a GM (4008), or null.
 * @returns The failure.
 * @example
 * closeFailure(4001, "auth", true).detail;
 * // "closed: 4001 auth"
 */
export function closeFailure(code: number, reason: string, account: boolean, told: CloseRow | null = null): LoadingFailure {
  const notice = closeNotice(code, reason, account, told);
  if (code === CloseCode.AuthFailed) {
    return { category: "sign-in", title: say("loading.failure.signIn"), sentence: notice.sentence, detail: notice.detail, action: notice.action === "log-in" ? "log-in" : "retry" };
  }
  const title = WITHOUT_SERVER.has(code) ? say("loading.failure.unreachable") : (joinTitle(code, reason, account) ?? refusedTitle(code));
  return { category: "server", title, sentence: notice.sentence, detail: notice.detail, action: notice.action === "reload" || notice.action === "status" ? notice.action : "retry" };
}

/**
 * The failure for a server that did not welcome the page within the deadline, or welcomed it and sent no
 * snapshot within the deadline after, as a server that is unreachable: a socket to a host that never answers
 * can wait on the browser's own timeout for minutes, and a server that sends no state leaves nothing to
 * reveal.
 *
 * @param deadlineMs The deadline in milliseconds.
 * @param awaited What the server did not send: the welcome or the first snapshot.
 * @returns The failure, naming what was missing and the deadline rounded up to whole seconds.
 * @example
 * silenceFailure(10_000, "snapshot").detail;
 * // "no snapshot within 10 s"
 */
export function silenceFailure(deadlineMs: number, awaited: JoinAwait): LoadingFailure {
  return { category: "server", title: say("loading.failure.unreachable"), sentence: say("loading.failure.unreachableSentence"), detail: say("loading.failure.silence", { awaited, seconds: String(Math.ceil(deadlineMs / 1000)) }), action: "retry" };
}

/**
 * The failure for a handoff to another map that never landed: the notice with Retry, which joins again by the normal path. It has no detail line: the player reads two short lines, and the cause is in the report (`gameReport.closed`).
 *
 * @returns The failure.
 */
export function handoffStalledFailure(): LoadingFailure {
  return { category: "server", title: say("loading.failure.handoff"), sentence: say("loading.failure.handoffSentence"), detail: "", action: "retry" };
}

/**
 * The failure for a file the world needs that did not arrive or could not be read: its pointer, a manifest,
 * the contract, an atlas page, a character strip or a chunk.
 *
 * @param error What the page threw.
 * @returns The failure, the page's message on its detail line.
 */
export function worldFailure(error: unknown): LoadingFailure {
  const detail = error instanceof Error ? (error.message === "" ? error.name : error.message) : String(error);
  return { category: "world", title: say("loading.failure.world"), sentence: say("loading.failure.worldSentence"), detail, action: "retry" };
}

/**
 * A disconnect notice as the loading shell shows it.
 *
 * @param category The notice's category.
 * @param notice The notice.
 * @returns The failure, its button the notice's action.
 */
function shellNotice(category: LoadingFailure["category"], notice: CloseNotice): LoadingFailure {
  return { category, title: notice.title, sentence: notice.sentence, detail: notice.detail, action: notice.action };
}

/**
 * The notice for a close after the world was shown (P1.9-T15): the disconnect table's, whole.
 *
 * @param code The close code.
 * @param reason The server's reason, which may be empty.
 * @param account Whether the page plays an account session rather than a `?token=` one.
 * @param told What the gateway told the page just before a close by a GM (4008), or null.
 * @returns The failure, in the `disconnected` category.
 * @example
 * disconnectFailure(1006, "", true).action;
 * // "reconnect"
 */
export function disconnectFailure(code: number, reason: string, account: boolean, told: CloseRow | null = null): LoadingFailure {
  return shellNotice("disconnected", closeNotice(code, reason, account, told));
}

/**
 * The notice for a sign-in that ended while the page played (P1.9-T15), which takes the page out of the
 * world as a close does.
 *
 * @param end Why the sign-in ended.
 * @returns The failure, in the `sign-in` category.
 */
export function signInFailure(end: SignInEnd): LoadingFailure {
  return shellNotice("sign-in", signInNotice(end));
}
